import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Mail,
  Settings,
  BarChart3,
  AlertTriangle,
  Wrench,
  RefreshCw,
  Plus,
  Check,
  X,
  Eye,
  EyeOff,
  Send,
  Globe,
  Shield,
  Zap,
  ChevronRight,
  ExternalLink,
  Info,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowUpRight,
  Trash2,
  Copy,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============ STATUS BADGE COMPONENT ============
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
    configured: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Configured' },
    not_configured: { bg: 'bg-zinc-600/50', text: 'text-zinc-400', label: 'Not Connected' },
    test_failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Test Failed' },
    inactive: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Inactive' },
    connected: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Connected' },
    pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Pending' },
  };

  const config = statusConfig[status] || statusConfig.not_configured;

  return (
    <Badge className={`${config.bg} ${config.text} border-0`}>
      {config.label}
    </Badge>
  );
};

// ============ PROVIDER CARD COMPONENT ============
const ProviderCard = ({ provider, isSelected, onSelect, onManage }) => {
  const isDefault = provider.provider === 'platform_default';
  
  return (
    <div
      onClick={onSelect}
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
        isSelected
          ? 'border-amber-500 bg-amber-500/10'
          : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
      }`}
      data-testid={`provider-card-${provider.provider}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${isDefault ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
            {isDefault ? (
              <Shield className={`w-5 h-5 ${isDefault ? 'text-emerald-400' : 'text-amber-400'}`} />
            ) : (
              <Mail className="w-5 h-5 text-amber-400" />
            )}
          </div>
          <div>
            <h4 className="font-semibold text-white">{provider.name}</h4>
            {provider.domain && (
              <p className="text-xs text-zinc-400 font-mono">{provider.domain}</p>
            )}
          </div>
        </div>
        {isSelected && (
          <div className="p-1 rounded-full bg-amber-500">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
      
      <p className="text-sm text-zinc-400 mb-3">{provider.description}</p>
      
      <div className="flex items-center justify-between">
        <StatusBadge status={provider.status} />
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onManage();
          }}
          className="text-zinc-400 hover:text-white"
        >
          {provider.configured ? 'Manage Configuration' : 'Add Service'}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============ MASKED API KEY FIELD ============
const MaskedApiKeyField = ({ maskedKey, onReplace, showReplace }) => {
  if (!maskedKey) return null;
  
  return (
    <div className="space-y-2">
      <Label className="text-zinc-400">API Key</Label>
      <div className="flex items-center gap-2">
        <div className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg font-mono text-sm text-zinc-300">
          {maskedKey}
        </div>
        {showReplace && (
          <Button
            variant="outline"
            size="sm"
            onClick={onReplace}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
          >
            Replace Key
          </Button>
        )}
      </div>
    </div>
  );
};

// ============ MAILGUN CONFIG MODAL ============
const MailgunConfigModal = ({ isOpen, onClose, existingConfig, onSave }) => {
  const [formData, setFormData] = useState({
    api_key: '',
    mailgun_domain: '',
    from_name: '',
    from_email: '',
    reply_to_email: '',
    region: 'US',
    is_active: false,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [replaceKey, setReplaceKey] = useState(!existingConfig?.api_key_masked);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingConfig) {
      setFormData({
        api_key: '',
        mailgun_domain: existingConfig.domain || '',
        from_name: existingConfig.from_name || '',
        from_email: existingConfig.from_email || '',
        reply_to_email: existingConfig.reply_to_email || '',
        region: existingConfig.region || 'US',
        is_active: existingConfig.is_active || false,
      });
      setReplaceKey(!existingConfig.api_key_masked);
    }
  }, [existingConfig, isOpen]);

  const handleSave = async () => {
    if (!formData.mailgun_domain || !formData.from_name || !formData.from_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (replaceKey && !formData.api_key) {
      toast.error('Please enter the Mailgun API key');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        ...formData,
        api_key: replaceKey ? formData.api_key : undefined,
      });
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Mail className="w-5 h-5 text-amber-500" />
            {existingConfig?.configured ? 'Manage Mailgun Configuration' : 'Connect Mailgun'}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Configure your agency's Mailgun email provider for branded client communications.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* API Key */}
          {existingConfig?.api_key_masked && !replaceKey ? (
            <MaskedApiKeyField
              maskedKey={existingConfig.api_key_masked}
              onReplace={() => setReplaceKey(true)}
              showReplace={true}
            />
          ) : (
            <div className="space-y-2">
              <Label className="text-zinc-400">
                Mailgun Private API Key <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <Input
                  type={showApiKey ? 'text' : 'password'}
                  placeholder="Enter Mailgun private API key"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-zinc-500">
                Find your API key at{' '}
                <a href="https://app.mailgun.com/app/account/security/api_keys" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">
                  Mailgun Dashboard <ExternalLink className="w-3 h-3 inline" />
                </a>
              </p>
            </div>
          )}

          {/* Domain */}
          <div className="space-y-2">
            <Label className="text-zinc-400">
              Mailgun Domain <span className="text-red-400">*</span>
            </Label>
            <Input
              placeholder="mg.yourdomain.com"
              value={formData.mailgun_domain}
              onChange={(e) => setFormData({ ...formData, mailgun_domain: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* From Name & Email */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">
                From Name <span className="text-red-400">*</span>
              </Label>
              <Input
                placeholder="Your Agency Name"
                value={formData.from_name}
                onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">
                From Email <span className="text-red-400">*</span>
              </Label>
              <Input
                type="email"
                placeholder="support@yourdomain.com"
                value={formData.from_email}
                onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>

          {/* Reply-To & Region */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Reply-To Email</Label>
              <Input
                type="email"
                placeholder="Same as from email"
                value={formData.reply_to_email}
                onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Region</Label>
              <Select
                value={formData.region}
                onValueChange={(v) => setFormData({ ...formData, region: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="EU">European Union</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active Toggle */}
          {existingConfig?.last_test_status === 'success' && (
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
              <div>
                <Label className="text-white">Activate Provider</Label>
                <p className="text-xs text-zinc-400">Use this provider for agency emails</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ TEST EMAIL MODAL ============
const TestEmailModal = ({ isOpen, onClose, onSend, defaultEmail }) => {
  const [email, setEmail] = useState(defaultEmail || '');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    setSending(true);
    try {
      await onSend(email);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Send className="w-5 h-5 text-amber-500" />
            Send Test Email
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Send a test email to verify your Mailgun configuration is working correctly.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Label className="text-zinc-400">Recipient Email</Label>
          <Input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white mt-2"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            {sending ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ EMAIL PROVIDER TAB ============
const EmailProviderTab = ({ data, onRefresh }) => {
  const { token, user } = useAuth();
  const [selectedProvider, setSelectedProvider] = useState(data?.active_provider || 'platform_default');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);

  const providers = data?.providers || {};
  const mailgunConfig = providers.mailgun || {};
  const platformConfig = providers.platform_default || {};
  
  const isMailgunActive = mailgunConfig.is_active;

  const handleSaveMailgun = async (formData) => {
    try {
      await axios.post(`${API}/agency/settings/email-services/mailgun`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mailgun configuration saved');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
      throw err;
    }
  };

  const handleSendTest = async (email) => {
    try {
      const res = await axios.post(`${API}/agency/settings/email-services/mailgun/test`, {
        recipient_email: email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success('Test email sent successfully!');
        onRefresh();
      } else {
        toast.error(res.data.error || 'Test email failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
      throw err;
    }
  };

  const handleActivate = async (activate) => {
    try {
      await axios.post(`${API}/agency/settings/email-services/mailgun/activate?activate=${activate}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Mailgun ${activate ? 'activated' : 'deactivated'}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update activation');
    }
  };

  return (
    <div className="space-y-6">
      {/* Fallback Warning Banner */}
      {!isMailgunActive && (
        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
          <div>
            <p className="font-medium text-amber-300">Agency Email Provider Not Active</p>
            <p className="text-sm text-amber-400/80">
              Client emails are currently being sent through the FundElite platform fallback sender.
            </p>
          </div>
        </div>
      )}
      
      {isMailgunActive && (
        <div className="flex items-start gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <p className="font-medium text-emerald-300">Agency Mailgun Active</p>
            <p className="text-sm text-emerald-400/80">
              Client and self-repair emails are being sent from your agency sender identity.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Email Provider</h3>
          <p className="text-sm text-zinc-400">
            Connect your agency email provider for branded client communications.
          </p>
        </div>
        <Button
          onClick={() => setConfigModalOpen(true)}
          className="bg-amber-500 hover:bg-amber-600 text-black"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Service
        </Button>
      </div>

      {/* Provider Selection */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ProviderCard
          provider={{
            ...platformConfig,
            provider: 'platform_default',
            name: 'FundElite Default Email System',
            description: 'Platform-managed email sending. Used as fallback when agency Mailgun is unavailable.',
            status: 'active',
            is_fallback: true,
          }}
          isSelected={!isMailgunActive}
          onSelect={() => isMailgunActive && handleActivate(false)}
          onManage={() => {}}
        />
        
        <ProviderCard
          provider={{
            ...mailgunConfig,
            provider: 'mailgun',
            name: 'Agency Mailgun',
            description: 'Custom email sending using your agency\'s Mailgun account for branded communications.',
            domain: mailgunConfig.domain,
          }}
          isSelected={isMailgunActive}
          onSelect={() => mailgunConfig.configured && mailgunConfig.last_test_status === 'success' && handleActivate(true)}
          onManage={() => setConfigModalOpen(true)}
        />
      </div>

      {/* Selected Provider Details */}
      {mailgunConfig.configured && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-400" />
              Mailgun Configuration
            </h4>
            <div className="flex items-center gap-2">
              <StatusBadge status={mailgunConfig.status} />
              {mailgunConfig.last_test_status && (
                <Badge className={mailgunConfig.last_test_status === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                  Test: {mailgunConfig.last_test_status}
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-xs text-zinc-500 uppercase">Sending Domain</p>
              <p className="text-sm text-white font-mono">{mailgunConfig.domain || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">From Name</p>
              <p className="text-sm text-white">{mailgunConfig.from_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">From Email</p>
              <p className="text-sm text-white">{mailgunConfig.from_email || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Reply-To</p>
              <p className="text-sm text-white">{mailgunConfig.reply_to_email || mailgunConfig.from_email || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Region</p>
              <p className="text-sm text-white">{mailgunConfig.region || 'US'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Last Tested</p>
              <p className="text-sm text-white">
                {mailgunConfig.last_test_at
                  ? new Date(mailgunConfig.last_test_at).toLocaleString()
                  : 'Never'}
              </p>
            </div>
          </div>

          {/* API Key Masked */}
          {mailgunConfig.api_key_masked && (
            <div className="mb-4">
              <p className="text-xs text-zinc-500 uppercase mb-1">API Key</p>
              <div className="flex items-center gap-2">
                <code className="px-3 py-1.5 bg-zinc-900 rounded text-sm text-zinc-300 font-mono">
                  {mailgunConfig.api_key_masked}
                </code>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-zinc-700">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfigModalOpen(true)}
              className="border-zinc-600 text-zinc-300"
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit Configuration
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTestModalOpen(true)}
              className="border-zinc-600 text-zinc-300"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Test Email
            </Button>
            {!mailgunConfig.is_active && mailgunConfig.last_test_status === 'success' && (
              <Button
                size="sm"
                onClick={() => handleActivate(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <Zap className="w-4 h-4 mr-2" />
                Activate
              </Button>
            )}
            {mailgunConfig.is_active && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleActivate(false)}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-2" />
                Deactivate
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Email Routing Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4">
          <h5 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Agency Mailgun Controls
          </h5>
          <ul className="space-y-1.5">
            {data?.email_routing?.agency_mailgun_controls?.map((item, i) => (
              <li key={i} className="text-sm text-zinc-400 flex items-center gap-2">
                <Check className="w-3 h-3 text-emerald-400" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4">
          <h5 className="text-sm font-medium text-emerald-400 mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Platform Default Controls
          </h5>
          <ul className="space-y-1.5">
            {data?.email_routing?.platform_default_controls?.map((item, i) => (
              <li key={i} className="text-sm text-zinc-400 flex items-center gap-2">
                <Check className="w-3 h-3 text-blue-400" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Modals */}
      <MailgunConfigModal
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        existingConfig={mailgunConfig.configured ? mailgunConfig : null}
        onSave={handleSaveMailgun}
      />
      <TestEmailModal
        isOpen={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        onSend={handleSendTest}
        defaultEmail={user?.email}
      />
    </div>
  );
};

// ============ REPLY & FORWARD TAB ============
const ReplyForwardTab = ({ data, onRefresh }) => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    forwarding_addresses: [],
    bcc_addresses: [],
    reply_to_addresses: [],
    forward_to_assigned_user: false,
  });
  const [newForward, setNewForward] = useState('');
  const [newBcc, setNewBcc] = useState('');
  const [newReply, setNewReply] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.forward_settings) {
      setSettings(data.forward_settings);
    }
  }, [data]);

  const addEmail = (field, email, setInput) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (settings[field].includes(email)) {
      toast.error('Email already added');
      return;
    }
    setSettings({ ...settings, [field]: [...settings[field], email] });
    setInput('');
  };

  const removeEmail = (field, email) => {
    setSettings({ ...settings, [field]: settings[field].filter(e => e !== email) });
  };

  const handleSave = async (field) => {
    setSaving(true);
    try {
      await axios.post(`${API}/agency/settings/email-services/reply-forward`, {
        [field]: settings[field],
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleForward = async (value) => {
    setSettings({ ...settings, forward_to_assigned_user: value });
    try {
      await axios.post(`${API}/agency/settings/email-services/reply-forward`, {
        forward_to_assigned_user: value,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save');
      setSettings({ ...settings, forward_to_assigned_user: !value });
    }
  };

  const EmailListSection = ({ title, description, note, field, emails, newValue, setNewValue, onAdd, onRemove }) => (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-white">{title}</h4>
          <p className="text-sm text-zinc-400 mt-1">{description}</p>
          {note && (
            <p className="text-xs text-amber-400/80 mt-2 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              {note}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="Enter email address"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onAdd()}
            className="bg-zinc-900 border-zinc-700 text-white"
          />
          <Button
            variant="outline"
            onClick={onAdd}
            className="border-zinc-600 text-zinc-300"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {emails.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {emails.map((email) => (
              <div
                key={email}
                className="flex items-center gap-2 px-3 py-1.5 bg-zinc-900 rounded-lg text-sm text-zinc-300"
              >
                <span>{email}</span>
                <button
                  onClick={() => onRemove(email)}
                  className="text-zinc-500 hover:text-red-400"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            size="sm"
            onClick={() => handleSave(field)}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <EmailListSection
        title="Forwarding Address"
        description="You will receive the email replies not only in the Conversation view, but also in your personal email inbox."
        note="Forwarding Addresses cannot use the same domain as your Sending Domain to prevent infinite loops."
        field="forwarding_addresses"
        emails={settings.forwarding_addresses}
        newValue={newForward}
        setNewValue={setNewForward}
        onAdd={() => addEmail('forwarding_addresses', newForward, setNewForward)}
        onRemove={(e) => removeEmail('forwarding_addresses', e)}
      />

      <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="font-semibold text-white">BCC Emails</h4>
            <p className="text-sm text-zinc-400 mt-1">
              Send a blind copy of outgoing client emails to internal addresses.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Enter email address"
              value={newBcc}
              onChange={(e) => setNewBcc(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addEmail('bcc_addresses', newBcc, setNewBcc)}
              className="bg-zinc-900 border-zinc-700 text-white"
            />
            <Button
              variant="outline"
              onClick={() => addEmail('bcc_addresses', newBcc, setNewBcc)}
              className="border-zinc-600 text-zinc-300"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {settings.bcc_addresses.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {settings.bcc_addresses.map((email) => (
                <div
                  key={email}
                  className="flex items-center gap-2 px-3 py-1.5 bg-zinc-900 rounded-lg text-sm text-zinc-300"
                >
                  <span>{email}</span>
                  <button
                    onClick={() => removeEmail('bcc_addresses', email)}
                    className="text-zinc-500 hover:text-red-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Forward to Assigned User Toggle */}
          <div className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg mt-4">
            <div>
              <Label className="text-white">Forward to assigned user</Label>
              <p className="text-xs text-zinc-400">Send replies to the assigned staff member when available</p>
            </div>
            <Switch
              checked={settings.forward_to_assigned_user}
              onCheckedChange={handleToggleForward}
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              onClick={() => handleSave('bcc_addresses')}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <EmailListSection
        title="Reply Address"
        description="Reply-to address will be added to all the emails. This controls where replies are routed when clients respond."
        note="Reply conversations cannot be tracked and will not come to conversation thread."
        field="reply_to_addresses"
        emails={settings.reply_to_addresses}
        newValue={newReply}
        setNewValue={setNewReply}
        onAdd={() => addEmail('reply_to_addresses', newReply, setNewReply)}
        onRemove={(e) => removeEmail('reply_to_addresses', e)}
      />
    </div>
  );
};

// ============ EMAIL ANALYTICS TAB ============
const EmailAnalyticsTab = ({ agencyId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agency/settings/email-services/analytics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ icon: Icon, label, value, color, percentage }) => (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-3`}>
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-sm text-zinc-400">{label}</p>
      <div className="flex items-end gap-2">
        <p className="text-3xl font-bold text-white">{value}</p>
        {percentage !== undefined && (
          <span className="text-sm text-zinc-500 mb-1">{percentage}%</span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!data?.has_data) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center mx-auto mb-4">
          <BarChart3 className="w-8 h-8 text-zinc-500" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">No Email Activity Found</h3>
        <p className="text-zinc-400 max-w-md mx-auto">
          {data?.message || 'Connect Mailgun webhooks to populate analytics.'}
        </p>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const total = Object.values(metrics).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6">
      {/* Date Filter */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Email Metrics</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-400">
            {data?.period_start && new Date(data.period_start).toLocaleDateString()} →{' '}
            {data?.period_end && new Date(data.period_end).toLocaleDateString()}
          </span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={Send}
          label="Sent"
          value={metrics.sent || 0}
          color="bg-blue-500/20 text-blue-400"
          percentage={Math.round((metrics.sent / total) * 100)}
        />
        <MetricCard
          icon={CheckCircle2}
          label="Delivered"
          value={metrics.delivered || 0}
          color="bg-emerald-500/20 text-emerald-400"
          percentage={Math.round((metrics.delivered / total) * 100)}
        />
        <MetricCard
          icon={Eye}
          label="Opened"
          value={metrics.opened || 0}
          color="bg-teal-500/20 text-teal-400"
          percentage={Math.round((metrics.opened / total) * 100)}
        />
        <MetricCard
          icon={ArrowUpRight}
          label="Clicked"
          value={metrics.clicked || 0}
          color="bg-cyan-500/20 text-cyan-400"
          percentage={Math.round((metrics.clicked / total) * 100)}
        />
        <MetricCard
          icon={AlertTriangle}
          label="Complained"
          value={metrics.complained || 0}
          color="bg-red-500/20 text-red-400"
          percentage={Math.round((metrics.complained / total) * 100)}
        />
        <MetricCard
          icon={XCircle}
          label="Bounced"
          value={metrics.bounced || 0}
          color="bg-orange-500/20 text-orange-400"
          percentage={Math.round((metrics.bounced / total) * 100)}
        />
        <MetricCard
          icon={X}
          label="Unsubscribed"
          value={metrics.unsubscribed || 0}
          color="bg-amber-500/20 text-amber-400"
          percentage={Math.round((metrics.unsubscribed / total) * 100)}
        />
        <MetricCard
          icon={AlertCircle}
          label="Failed"
          value={metrics.failed || 0}
          color="bg-rose-500/20 text-rose-400"
          percentage={Math.round((metrics.failed / total) * 100)}
        />
      </div>
    </div>
  );
};

// ============ BOUNCE CLASSIFICATION TAB ============
const BounceClassificationTab = ({ agencyId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchBounces();
  }, [days]);

  const fetchBounces = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agency/settings/email-services/bounces?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch bounces:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Email Bounces</h3>
          <p className="text-sm text-zinc-400">Track and classify email delivery issues</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
          <p className="text-sm text-zinc-400 flex items-center gap-1">
            Permanent Bounce
            <Info className="w-3 h-3" />
          </p>
          <p className="text-3xl font-bold text-white">{summary.permanent_bounce || 0}</p>
        </div>
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
          <p className="text-sm text-zinc-400">Permanent Bounce Rate</p>
          <p className="text-3xl font-bold text-white">{summary.permanent_bounce_rate || 0}%</p>
        </div>
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
          <p className="text-sm text-zinc-400 flex items-center gap-1">
            ESP/ISP Block
            <Info className="w-3 h-3" />
          </p>
          <p className="text-3xl font-bold text-white">{summary.esp_block || 0}</p>
        </div>
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
          <p className="text-sm text-zinc-400 flex items-center gap-1">
            Delivered
            <Info className="w-3 h-3" />
          </p>
          <p className="text-3xl font-bold text-white">{summary.delivered || 0}</p>
        </div>
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
          <p className="text-sm text-zinc-400">Delivery Rate</p>
          <p className="text-3xl font-bold text-emerald-400">{summary.delivery_rate || 0}%</p>
        </div>
      </div>

      {/* Bounce Table */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-700 flex items-center justify-between">
          <h4 className="font-medium text-white">Bounce Classification Overview</h4>
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-400 hover:text-amber-300"
          >
            Troubleshoot Email Bounces
          </Button>
        </div>

        {!data?.has_data || data?.bounces?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-zinc-400">No bounce issues were found for the selected date and filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">ESP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Error Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Count</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {data?.bounces?.map((bounce, i) => (
                  <tr key={i} className="border-t border-zinc-700/50">
                    <td className="px-4 py-3 text-sm text-white">{bounce.esp_name || '-'}</td>
                    <td className="px-4 py-3">
                      <Badge className="bg-red-500/20 text-red-400">{bounce.category}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-400 font-mono">{bounce.error_code || '-'}</td>
                    <td className="px-4 py-3 text-sm text-white font-semibold">{bounce.count}</td>
                    <td className="px-4 py-3 text-sm text-zinc-400">
                      {bounce.last_seen ? new Date(bounce.last_seen).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ POSTMASTER TOOLS TAB ============
const PostmasterToolsTab = ({ agencyId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchPostmaster();
  }, []);

  const fetchPostmaster = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agency/settings/email-services/postmaster`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch postmaster:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const PostmasterCard = ({ title, status, message, domains, onConnect }) => (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-500/20">
            <Globe className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h4 className="font-semibold text-white">{title}</h4>
            <StatusBadge status={status} />
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onConnect}
          disabled={status === 'connected'}
          className="border-zinc-600 text-zinc-300"
        >
          {status === 'connected' ? 'Connected' : 'Connect'}
        </Button>
      </div>
      <p className="text-sm text-zinc-400">{message}</p>
      
      {domains?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-700">
          <p className="text-xs text-zinc-500 uppercase mb-2">Dedicated Sending Domains</p>
          <div className="flex flex-wrap gap-2">
            {domains.map((domain, i) => (
              <Badge key={i} className="bg-zinc-700 text-zinc-300">{domain}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-1">Postmaster Tools</h3>
        <p className="text-sm text-zinc-400">Monitor your email sender reputation across major providers.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PostmasterCard
          title="Google Postmaster"
          status={data?.google_postmaster?.status || 'not_connected'}
          message={data?.google_postmaster?.message || 'Connect to monitor Gmail delivery reputation.'}
          domains={data?.google_postmaster?.domains}
          onConnect={() => toast.info('Google Postmaster integration coming soon')}
        />
        <PostmasterCard
          title="Microsoft SNDS"
          status={data?.microsoft_snds?.status || 'not_connected'}
          message={data?.microsoft_snds?.message || 'Connect to monitor Outlook/Hotmail IP reputation.'}
          onConnect={() => toast.info('Microsoft SNDS integration coming soon')}
        />
      </div>

      {/* Sending Domain Info */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
        <h4 className="font-semibold text-white mb-4">Dedicated Sending Domain</h4>
        
        {data?.sending_domain ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <code className="px-3 py-1.5 bg-zinc-900 rounded text-sm text-amber-400 font-mono">
                {data.sending_domain}
              </code>
              <StatusBadge status={data?.dns_verification?.connected ? 'connected' : 'not_configured'} />
            </div>
            
            <p className="text-sm text-zinc-400">
              {data?.dns_verification?.message || 'Verify your domain DNS records to improve deliverability.'}
            </p>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-zinc-400">No sending domain configured. Set up Mailgun first.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ MAIN PAGE COMPONENT ============
const AgencyEmailServicesPage = () => {
  const { agencyId } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('provider');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agency/settings/email-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch email services:', err);
      toast.error('Failed to load email services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="text-white p-6 lg:p-8" data-testid="agency-email-services-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 rounded-xl">
            <Mail className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Email Services</h1>
            <p className="text-sm text-zinc-400">
              Configure email providers and manage delivery settings
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          className="border-zinc-600 text-zinc-300"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1 mb-6 flex-wrap">
          <TabsTrigger
            value="provider"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-zinc-400"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email Provider
          </TabsTrigger>
          <TabsTrigger
            value="forward"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-zinc-400"
          >
            <Settings className="w-4 h-4 mr-2" />
            Reply & Forward
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-zinc-400"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Email Analytics
          </TabsTrigger>
          <TabsTrigger
            value="bounces"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-zinc-400"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Bounce Classification
          </TabsTrigger>
          <TabsTrigger
            value="postmaster"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-zinc-400"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Postmaster Tools
          </TabsTrigger>
        </TabsList>

        <TabsContent value="provider" className="mt-0">
          <EmailProviderTab data={data} onRefresh={fetchData} />
        </TabsContent>

        <TabsContent value="forward" className="mt-0">
          <ReplyForwardTab data={data} onRefresh={fetchData} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-0">
          <EmailAnalyticsTab agencyId={agencyId} />
        </TabsContent>

        <TabsContent value="bounces" className="mt-0">
          <BounceClassificationTab agencyId={agencyId} />
        </TabsContent>

        <TabsContent value="postmaster" className="mt-0">
          <PostmasterToolsTab agencyId={agencyId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AgencyEmailServicesPage;
